import {
  CalendarEventTypeId,
  MessageTypeId,
  ProcedureAddressee,
  ProcedureExecutionTypeId,
  ProcedureTypeId,
  RecipientType,
} from '../../pages/Calendar/Calendar.types';

export const ONE_DAY_TIME = 1000 * 3600 * 24;

export const getEventName = (eventType: CalendarEventTypeId, assessmentSessionName?: string) => {
  switch (eventType) {
    case CalendarEventTypeId.assessmentSession:
      return `Sesja oceniania: ${assessmentSessionName}`;
    case CalendarEventTypeId.trainingProcess:
      return 'Proces ćwiczeniowy';
    case CalendarEventTypeId.qualificationProcess:
      return 'Proces kwalifikacji';
    case CalendarEventTypeId.scoringProcess:
      return 'Proces oceniania';
  }
};

export const getEventBadgeColor = (eventType: CalendarEventTypeId) => {
  switch (eventType) {
    case CalendarEventTypeId.assessmentSession:
      return '#E0E7FF';
    case CalendarEventTypeId.trainingProcess:
      return '#EBDFFB';
    case CalendarEventTypeId.qualificationProcess:
      return '#FFE1E1';
    case CalendarEventTypeId.scoringProcess:
      return '#E0F4E3';
  }
};

export const getEventTextColor = (eventType: CalendarEventTypeId) => {
  switch (eventType) {
    case CalendarEventTypeId.assessmentSession:
      return '#3560FF';
    case CalendarEventTypeId.trainingProcess:
      return '#9E52FF';
    case CalendarEventTypeId.qualificationProcess:
      return '#BC0000';
    case CalendarEventTypeId.scoringProcess:
      return '#347F42';
  }
};

export const getAddProcedureButtonColor = (eventType: CalendarEventTypeId) => {
  switch (eventType) {
    case CalendarEventTypeId.assessmentSession:
      return 'rgba(53, 96, 255, 0.5)';
    case CalendarEventTypeId.trainingProcess:
      return 'rgba(158, 82, 255, 0.5)';
    case CalendarEventTypeId.qualificationProcess:
      return 'rgba(188, 0, 0, 0.5)';
    case CalendarEventTypeId.scoringProcess:
      return 'rgba(52, 127, 66, 0.5)';
  }
};

export const getProcedureStartTypeLabel = (procedureStartType: ProcedureTypeId) => {
  switch (procedureStartType) {
    case ProcedureTypeId.manual:
      return 'Ręcznie';
    case ProcedureTypeId.automatic:
      return 'Automatycznie';
  }
};

export const getProcedureStartTimeLabel = (procedureStartTime: ProcedureExecutionTypeId) => {
  switch (procedureStartTime) {
    case ProcedureExecutionTypeId.beforeEventStartDate:
      return 'Przed terminem rozpoczęcia';
    case ProcedureExecutionTypeId.atEventStartDate:
      return 'W terminie rozpoczęcia';
    case ProcedureExecutionTypeId.atEventEndDate:
      return 'W terminie zakończenia';
    case ProcedureExecutionTypeId.beforeEventEndDate:
      return 'Przed terminem zakończenia';
  }
};

export const getProcedureNotificationTypeLabel = (messageTypeId: MessageTypeId) => {
  switch (messageTypeId) {
    case MessageTypeId.push:
      return 'Powiadomienie push';
    case MessageTypeId.one:
      return 'Wiadomość wewnętrzna';
    case MessageTypeId.email:
      return 'E-mail';
    case MessageTypeId.sms:
      return 'SMS';
  }
};

export const getProcedureRecipientLabel = (recipientType: string | RecipientType) => {
  switch (recipientType) {
    case RecipientType.examiner:
      return 'E';
    case RecipientType.examiningTeamChairman:
      return 'PZE';
    case RecipientType.examTaskGroupCoordinator:
      return 'KGZ';
    case RecipientType.domesticCoordinator:
      return 'KK';
    case RecipientType.admin:
      return 'Administrator';
    case RecipientType.admin:
      return 'Administrator';
    case RecipientType.okeAdmin:
      return 'Pracownik Komisji';
    case RecipientType.helpdesk:
      return 'Helpdesk';
    default:
      return '';
  }
};

export const getProcedureAddressee = (procedureAddressee: ProcedureAddressee) => {
  const objectArray = Object.entries(procedureAddressee);
  const addressee: RecipientType[] = [];
  objectArray.forEach(([key, value]) => {
    if (value && key === 'admin') addressee.push(RecipientType.admin);
    if (value && key === 'helpdesk') addressee.push(RecipientType.helpdesk);
    if (value && key === 'domesticCoordinator') addressee.push(RecipientType.domesticCoordinator);
    if (value && key === 'examTaskGroupCoordinator') addressee.push(RecipientType.examTaskGroupCoordinator);
    if (value && key === 'examiningTeamChairman') addressee.push(RecipientType.examiningTeamChairman);
    if (value && key === 'examiner') addressee.push(RecipientType.examiner);
  });
  return addressee;
};

export const getProcedureRecipientsLabels = (recipients: RecipientType[]) => {
  return recipients.map((recipient) => getProcedureRecipientLabel(recipient)).join(', ');
};

export const getProcedureCompletedLabel = (procedureCompleted: boolean) => {
  switch (procedureCompleted) {
    case true:
      return 'TAK';
    case false:
      return 'NIE';
  }
};

export const formatMonth = (monthIndex: number) => {
  if (monthIndex < 9) {
    return `0${monthIndex + 1}`;
  } else {
    return `${monthIndex + 1}`;
  }
};

export const formatTime = (hours: number, minutes: number) => {
  const formattedHours = hours <= 10 ? `0${hours}` : `${hours}`;
  const formattedMinutes = minutes <= 10 ? `0${minutes}` : `${minutes}`;
  return `${formattedHours}:${formattedMinutes}`;
};

const getFormattedEventDate = (date: Date) => {
  return `${date.getDate()}.${formatMonth(date.getMonth())}`;
};

export const getProcedureFormattedDate = (date: Date) => {
  return `${date.getDate()}/${formatMonth(date.getMonth())}/${date.getFullYear()} ${formatTime(
    date.getHours(),
    date.getMinutes(),
  )}`;
};

const getFormattedEventDateAndTime = (date: Date) => {
  return `${date.getDate()}.${formatMonth(date.getMonth())} godz. ${formatTime(date.getHours(), date.getMinutes())}`;
};

export const getFormattedEventDuration = (eventStartDate: Date, eventEndDate: Date, eventType: CalendarEventTypeId) => {
  if (eventType !== CalendarEventTypeId.assessmentSession) {
    return `${getFormattedEventDateAndTime(eventStartDate)} - ${getFormattedEventDateAndTime(eventEndDate)}`;
  } else return `${getFormattedEventDate(eventStartDate)} - ${getFormattedEventDate(eventEndDate)}`;
};

export const getEventTooltipTitle = (
  eventType: CalendarEventTypeId,
  eventStartDate: Date,
  eventEndDate: Date,
  assessmentSessionName?: string,
  isOneDayProcess?: boolean,
) => {
  return `${getEventName(eventType, assessmentSessionName)} ${
    !isOneDayProcess
      ? getFormattedEventDuration(eventStartDate, eventEndDate, eventType)
      : getFormattedEventDate(eventStartDate)
  }`;
};
